(() => {
  const config = window.wtfSupportWidgetConfig || {};
  const root = document.getElementById('wtf-support-widget-root');

  if (!root) {
    return;
  }

  const state = {
    panelOpen: false,
    faqLoaded: false,
    loading: false,
    faqArticles: [],
    faqFilter: '',
    aiMode: false, // true = show AI chat, false = show FAQ
    aiAnswer: null, // stores AI response
    aiLoading: false,
    aiConversation: [], // Phase 2 Security: conversation history [{role: 'user'|'assistant', content: string}]
    // Honeypot & timing (anti-bot security)
    aiFirstInteraction: null, // Timestamp when user first interacts with AI input
    aiHoneypot: '', // Invisible field - should stay empty for humans
    aiHasTyped: false, // Track if user has typed - only clear on first focus
  };

  const dom = {
    launcher: null,
    panel: null,
    faqList: null,
    form: null,
    status: null,
    faqSearch: null,
  };

  const safeProxy = (path = '') => {
    if (!config.proxyEndpoint) {
      return null;
    }
    const base = config.proxyEndpoint.replace(/\/$/, '');
    return `${base}${path}`;
  };

  // Debounce utility for search input
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const getCustomerDefaults = () => {
    if (!config.customer) {
      return { name: '', email: '' };
    }
    return {
      name: config.customer.first_name ? `${config.customer.first_name} ${config.customer.last_name || ''}`.trim() : '',
      email: config.customer.email || '',
    };
  };

  const isMobile = () => window.innerWidth <= 768;

  const togglePanel = () => {
    state.panelOpen = !state.panelOpen;
    dom.panel.classList.toggle('is-open', state.panelOpen);
    dom.backdrop.classList.toggle('is-visible', state.panelOpen);
    
    // iOS 26 Safari Liquid Glass fix - set body background when modal opens (mobile only)
    if (isMobile()) {
      if (state.panelOpen) {
        document.body.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
      } else {
        document.body.style.backgroundColor = '';
      }
      // Hide launcher on mobile when panel is open
      dom.launcher.classList.toggle('is-hidden', state.panelOpen);
    }
    
    if (state.panelOpen) {
      loadEmbedSdk();
      // Load FAQs if not already loaded
      if (!state.faqLoaded) {
        loadFaqs();
      }
      // Scroll panel content to top when opening
      const panelBody = dom.panel.querySelector('.wtf-support-widget__panel-body');
      if (panelBody) {
        panelBody.scrollTop = 0;
      }
    } else {
      // Clear FAQ search when closing panel
      state.faqFilter = '';
      if (dom.faqSearch) {
        dom.faqSearch.value = '';
      }
      if (dom.faqList) {
        dom.faqList.innerHTML = '';
        dom.faqList.style.display = 'none';
      }
    }
  };

  const renderLauncher = () => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'wtf-support-widget__launcher';
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-label', 'Need help? Open support widget');
    button.innerHTML = `
      <svg width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"></path>
        <path fill="currentColor" d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"></path>
      </svg>
    `;
    button.addEventListener('click', () => {
      button.setAttribute('aria-expanded', String(!state.panelOpen));
      togglePanel();
    });
    dom.launcher = button;
    root.appendChild(button);
  };

  const renderBackdrop = () => {
    const backdrop = document.createElement('div');
    backdrop.className = 'wtf-support-widget__backdrop';
    backdrop.addEventListener('click', () => {
      if (state.panelOpen) {
        dom.launcher.setAttribute('aria-expanded', 'false');
        togglePanel();
      }
    });
    dom.backdrop = backdrop;
    root.appendChild(backdrop);
  };

  const renderPanel = () => {
    const wrapper = document.createElement('section');
    try {
      wrapper.className = 'wtf-support-widget__panel';
      wrapper.setAttribute('aria-hidden', 'true');
      wrapper.innerHTML = `
      <header class="wtf-support-widget__panel-header">
        <button type="button" class="wtf-support-widget__close" aria-label="Close support widget">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="wtf-support-widget__panel-header-content">
          ${config.logo ? `<img src="${config.logo}" alt="WTF Notebooks" class="wtf-support-widget__logo">` : ''}
          <div class="wtf-support-widget__panel-header-text">
            <div class="wtf-support-widget__panel-title">Need a hand?</div>
          </div>
        </div>
      </header>
      <div class="wtf-support-widget__panel-body">
        <!-- AI Chat Section -->
        <div class="wtf-support-widget__ai-chat">
          <div class="wtf-support-widget__faq-header">
            <h3>Ask us a question</h3>
            <p>Ask our virtual assistant about shipping, orders, or anything else.</p>
            <div class="wtf-support-widget__ai-input-wrapper">
              <label class="wtf-support-widget__faq-search" for="wtf-support-widget-ai-input">
                <span class="sr-only">Ask us a question</span>
                <input id="wtf-support-widget-ai-input" class="wtf-support-widget__faq-search-input" name="ai-question" type="text" placeholder="Type your question here..." aria-label="Ask us a question">
              </label>
              <button type="button" class="wtf-support-widget__ai-ask-btn">Send</button>
            </div>
            <!-- Honeypot field (invisible to humans, bots may fill it) -->
            <input id="wtf-ai-honeypot" type="text" name="website" tabindex="-1" autocomplete="off" style="position: absolute; left: -9999px; width: 1px; height: 1px;" aria-hidden="true">
            <div class="wtf-ai-response-container">
              <div class="wtf-support-widget__ai-response">
                <div class="wtf-support-widget__ai-answer"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- Original FAQ Section - DO NOT MODIFY - This is a backup in case AI chat fails -->
        <div class="wtf-support-widget__faq" style="display: none;">
          <div class="wtf-support-widget__faq-header">
            <h3>Quick Answers</h3>
            <p>Quickly search our FAQ for answers by typing a keyword e.g. "shipping", "tracking", "how" or "when".</p>
            <label class="wtf-support-widget__faq-search" for="wtf-support-widget-search">
              <span class="sr-only">Search quick answers</span>
              <input id="wtf-support-widget-search" class="wtf-support-widget__faq-search-input" name="faq-search" type="search" placeholder="Search for answers" aria-label="Search quick answers">
            </label>
            <ul class="wtf-support-widget__faq-list"></ul>
          </div>
        </div>
        <form class="wtf-support-widget__form">
          <h3>Contact Customer Support</h3>
          <p>Fill out the form below to contact us. We'll email you back as soon as we see your message.</p>
          <input id="wtf-support-name" name="name" autocomplete="name" placeholder="Name" required minlength="2" maxlength="100">
          <input id="wtf-support-email" type="email" name="email" autocomplete="email" placeholder="Email" required maxlength="255">
          <div class="wtf-support-widget__custom-select">
            <select id="wtf-support-subject" name="subject" required style="position: absolute; opacity: 0; pointer-events: none; width: 1px; height: 1px;">
              <option value="">Subject</option>
              <option value="Inquiry about my order">Inquiry about my order</option>
              <option value="Notebook title suggestion">Notebook title suggestion</option>
              <option value="Wholesale inquiry">Wholesale inquiry</option>
              <option value="General inquiry">General inquiry</option>
            </select>
            <button type="button" class="wtf-support-widget__select-trigger" aria-haspopup="listbox" aria-expanded="false">
              <span class="wtf-support-widget__select-text">Subject</span>
              <svg class="wtf-support-widget__select-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2 4L6 8L10 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <ul class="wtf-support-widget__select-options" role="listbox" style="display: none;">
              <li role="option" data-value="">Subject</li>
              <li role="option" data-value="Inquiry about my order">Inquiry about my order</li>
              <li role="option" data-value="Notebook title suggestion">Notebook title suggestion</li>
              <li role="option" data-value="Wholesale inquiry">Wholesale inquiry</li>
              <li role="option" data-value="General inquiry">General inquiry</li>
            </ul>
          </div>
          <div id="wtf-support-order-number-wrapper" style="display: none;">
            <div class="wtf-support-widget__order-number-prefix">
              <span class="wtf-support-widget__order-number-prefix-text">WTF/N</span>
              <input id="wtf-support-order-number" name="order_number" type="tel" inputmode="numeric" pattern="[0-9]*" placeholder="XXXXXX" maxlength="10" required>
            </div>
          </div>
          <textarea id="wtf-support-message" name="message" rows="4" placeholder="Message" required minlength="10" maxlength="2000"></textarea>
          <button type="submit">Send message</button>
          <p class="wtf-support-widget__status" role="status"></p>
        </form>
      </div>
    `;

    // Add CSS animations for AI chat
    const style = document.createElement('style');
    style.textContent = `
      /* Rainbow glow animation - colors chase clockwise with subtle pulsing */
      @keyframes wtf-rainbow-rotate {
        0% {
          box-shadow: inset 4px 3px 6px rgba(230, 135, 90, 0.4),
                      inset -4px 3px 6px rgba(230, 215, 90, 0.4),
                      inset -4px -3px 6px rgba(180, 135, 230, 0.4),
                      inset 4px -3px 6px rgba(90, 230, 180, 0.4);
        }
        12.5% {
          box-shadow: inset 5px 4px 6px rgba(230, 135, 90, 0.4),
                      inset -5px 4px 6px rgba(230, 215, 90, 0.4),
                      inset -5px -4px 6px rgba(180, 135, 230, 0.4),
                      inset 5px -4px 6px rgba(90, 230, 180, 0.4);
        }
        25% {
          box-shadow: inset 4px 3px 6px rgba(90, 230, 180, 0.4),
                      inset -4px 3px 6px rgba(230, 135, 90, 0.4),
                      inset -4px -3px 6px rgba(230, 215, 90, 0.4),
                      inset 4px -3px 6px rgba(180, 135, 230, 0.4);
        }
        37.5% {
          box-shadow: inset 5px 4px 6px rgba(90, 230, 180, 0.4),
                      inset -5px 4px 6px rgba(230, 135, 90, 0.4),
                      inset -5px -4px 6px rgba(230, 215, 90, 0.4),
                      inset 5px -4px 6px rgba(180, 135, 230, 0.4);
        }
        50% {
          box-shadow: inset 4px 3px 6px rgba(180, 135, 230, 0.4),
                      inset -4px 3px 6px rgba(90, 230, 180, 0.4),
                      inset -4px -3px 6px rgba(230, 135, 90, 0.4),
                      inset 4px -3px 6px rgba(230, 215, 90, 0.4);
        }
        62.5% {
          box-shadow: inset 5px 4px 6px rgba(180, 135, 230, 0.4),
                      inset -5px 4px 6px rgba(90, 230, 180, 0.4),
                      inset -5px -4px 6px rgba(230, 135, 90, 0.4),
                      inset 5px -4px 6px rgba(230, 215, 90, 0.4);
        }
        75% {
          box-shadow: inset 4px 3px 6px rgba(230, 215, 90, 0.4),
                      inset -4px 3px 6px rgba(180, 135, 230, 0.4),
                      inset -4px -3px 6px rgba(90, 230, 180, 0.4),
                      inset 4px -3px 6px rgba(230, 135, 90, 0.4);
        }
        87.5% {
          box-shadow: inset 5px 4px 6px rgba(230, 215, 90, 0.4),
                      inset -5px 4px 6px rgba(180, 135, 230, 0.4),
                      inset -5px -4px 6px rgba(90, 230, 180, 0.4),
                      inset 5px -4px 6px rgba(230, 135, 90, 0.4);
        }
        100% {
          box-shadow: inset 4px 3px 6px rgba(230, 135, 90, 0.4),
                      inset -4px 3px 6px rgba(230, 215, 90, 0.4),
                      inset -4px -3px 6px rgba(180, 135, 230, 0.4),
                      inset 4px -3px 6px rgba(90, 230, 180, 0.4);
        }
      }

      .wtf-ai-answer-card {
        background: #fef9e7;
        padding: 20px;
        border-radius: 8px;
        color: #333333;
        animation: wtf-rainbow-rotate 12s linear infinite;
        position: relative;
      }

      /* Paragraph spacing */
      .wtf-ai-answer-card p {
        margin: 0 0 0.8em 0;
        line-height: 1.6;
      }

      /* List formatting with proper indentation */
      .wtf-ai-answer-card ul,
      .wtf-ai-answer-card ol {
        margin: 0 0 0.8em 0;
        padding-left: 0;
        list-style-position: outside !important;
      }

      .wtf-ai-answer-card li {
        margin-left: 1em;
        padding-left: 0.5em;
        margin-bottom: 0.2em;
        list-style-position: outside !important;
      }

      /* reduce bottom margin from last text elements (confidence dot doesn't count) */
      .wtf-ai-answer-card > p:last-of-type {
        margin-bottom: 0.3em !important;
      }

      /* Confidence indicator dot */
      .wtf-confidence-dot {
        position: absolute;
        bottom: 12px;
        right: 12px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        display: inline-block;
      }

      /* CSS Grid container animation */
      .wtf-ai-response-container {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.6s ease-in-out;
        overflow: hidden;
      }

      .wtf-ai-response-container.visible {
        grid-template-rows: 1fr;
        margin-top: 5px;
      }

      .wtf-support-widget__ai-response {
        min-height: 0;
      }

      /* Answer fade-in */
      .wtf-support-widget__ai-answer {
        opacity: 0;
        transition: opacity 0.4s ease-in;
      }

      .wtf-support-widget__ai-answer.fade-in {
        opacity: 1;
      }

      /* Override faq-header padding for AI chat */
      .wtf-support-widget__ai-chat .wtf-support-widget__faq-header {
        padding-bottom: 16px;
      }

      /* AI input wrapper */
      .wtf-support-widget__ai-input-wrapper {
        display: flex;
        align-items: stretch;
      }

      .wtf-support-widget__ai-input-wrapper .wtf-support-widget__faq-search {
        flex: 1;
        margin: 0;
      }

      .wtf-support-widget__ai-input-wrapper #wtf-support-widget-ai-input {
        border-top-right-radius: 0px !important;
        border-bottom-right-radius: 0px !important;
        border-right: none !important;
      }

      .wtf-support-widget__ai-ask-btn {
        background: #D32F2F;
        color: white;
        border: none;
        padding: 0 24px;
        border-radius: 0px 50px 50px 0px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.3s ease;
        white-space: nowrap;
        height: auto;
        width: 115px; /* Fixed width to prevent stretching between 'Send' and 'Sending...' */
        text-align: center;
      }

      /* Thinking overlay - appears on top of answer card */
      .wtf-ai-thinking-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.2); /* Slight darkening overlay */
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        z-index: 10;
      }

      .wtf-ai-spinner {
        width: 40px;
        height: 40px;
        border: 4px solid transparent;
        border-top-color: #e68760; /* Peach */
        border-right-color: #e6d75a; /* Light yellow */
        border-bottom-color: #b487e6; /* Light purple */
        border-left-color: #5ae6b4; /* Aqua */
        border-radius: 50%;
        animation: wtf-spin 0.8s linear infinite;
      }

      @keyframes wtf-spin {
        to {
          transform: rotate(360deg);
        }
      }
    `;
    wrapper.appendChild(style);

    dom.panel = wrapper;
    dom.closeButton = wrapper.querySelector('.wtf-support-widget__close');
    dom.faqList = wrapper.querySelector('.wtf-support-widget__faq-list');
    dom.faqSearch = wrapper.querySelector('#wtf-support-widget-search');
    dom.form = wrapper.querySelector('.wtf-support-widget__form');
    dom.status = wrapper.querySelector('.wtf-support-widget__status');
    
    // AI chat elements
    dom.aiChat = wrapper.querySelector('.wtf-support-widget__ai-chat');
    dom.faqSection = wrapper.querySelector('.wtf-support-widget__faq');
    dom.aiInput = wrapper.querySelector('#wtf-support-widget-ai-input');
    dom.aiAskBtn = wrapper.querySelector('.wtf-support-widget__ai-ask-btn');
    dom.aiResponse = wrapper.querySelector('.wtf-support-widget__ai-response');
    dom.aiAnswer = wrapper.querySelector('.wtf-support-widget__ai-answer');
    dom.aiHoneypot = wrapper.querySelector('#wtf-ai-honeypot');
    
    // Add hover effect to AI Ask button
    if (dom.aiAskBtn) {
      dom.aiAskBtn.addEventListener('mouseenter', () => {
        dom.aiAskBtn.style.background = '#000';
      });
      dom.aiAskBtn.addEventListener('mouseleave', () => {
        if (!state.aiLoading) {
          dom.aiAskBtn.style.background = '#D32F2F';
        }
      });
    }
    
    dom.inputs = {
      name: dom.form?.querySelector('input[name="name"]'),
      email: dom.form?.querySelector('input[name="email"]'),
      subject: dom.form?.querySelector('select[name="subject"]'),
      orderNumber: dom.form?.querySelector('input[name="order_number"]'),
      orderNumberWrapper: dom.form?.querySelector('#wtf-support-order-number-wrapper'),
      message: dom.form?.querySelector('textarea[name="message"]'),
    };

    // Close button event listener
    if (dom.closeButton) {
      dom.closeButton.addEventListener('click', () => {
        if (state.panelOpen) {
          dom.launcher.setAttribute('aria-expanded', 'false');
          togglePanel();
        }
      });
    }

    // AI chat event listeners
    if (dom.aiAskBtn) {
      dom.aiAskBtn.addEventListener('click', handleAIQuestion);
    }

    if (dom.aiInput) {
      // Clear input only on FIRST focus - allows copy/paste workflow
      dom.aiInput.addEventListener('focus', () => {
        // Only clear if user hasn't typed anything yet
        if (!state.aiHasTyped) {
          dom.aiInput.value = '';
        }
        // Track first interaction for timing-based bot detection
        if (!state.aiFirstInteraction) {
          state.aiFirstInteraction = Date.now();
        }
      });
      
      // Track when user types - prevents clearing on subsequent focuses
      dom.aiInput.addEventListener('input', () => {
        if (dom.aiInput.value.length > 0) {
          state.aiHasTyped = true;
        }
      });
      
      dom.aiInput.addEventListener('keypress', (e) => {
        // Track first interaction for timing-based bot detection
        if (!state.aiFirstInteraction) {
          state.aiFirstInteraction = Date.now();
        }
        
        if (e.key === 'Enter' && !state.aiLoading) {
          handleAIQuestion();
          dom.aiInput.blur(); // Dismiss keyboard on mobile
        }
      });
    }


    // Custom dropdown elements
    const customSelect = dom.form?.querySelector('.wtf-support-widget__custom-select');
    const selectTrigger = customSelect?.querySelector('.wtf-support-widget__select-trigger');
    const selectText = customSelect?.querySelector('.wtf-support-widget__select-text');
    const selectOptions = customSelect?.querySelector('.wtf-support-widget__select-options');

    // Initialize custom dropdown
    if (dom.form && customSelect && selectTrigger && selectOptions && dom.inputs.subject) {
      // Update trigger text when select changes
      const updateTrigger = () => {
        const selectedOption = dom.inputs.subject.options[dom.inputs.subject.selectedIndex];
        selectText.textContent = selectedOption.value || 'Subject';
        selectTrigger.classList.toggle('has-value', !!selectedOption.value);
      };

      // Handle option clicks
      selectOptions.querySelectorAll('li').forEach((option) => {
        option.addEventListener('click', () => {
          const value = option.getAttribute('data-value');
          dom.inputs.subject.value = value;
          updateTrigger();
          selectOptions.style.display = 'none';
          selectTrigger.setAttribute('aria-expanded', 'false');
          selectTrigger.classList.remove('is-open');
          // Clear error state when subject is selected
          selectTrigger.classList.remove('is-invalid');
          
          // Trigger change event for order number logic
          const changeEvent = new Event('change', { bubbles: true });
          dom.inputs.subject.dispatchEvent(changeEvent);
        });
      });

      // Toggle dropdown
      selectTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = selectOptions.style.display === 'block';
        selectOptions.style.display = isOpen ? 'none' : 'block';
        selectTrigger.setAttribute('aria-expanded', String(!isOpen));
        selectTrigger.classList.toggle('is-open', !isOpen);
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!customSelect.contains(e.target)) {
          selectOptions.style.display = 'none';
          selectTrigger.setAttribute('aria-expanded', 'false');
          selectTrigger.classList.remove('is-open');
        }
      });

      // Initialize trigger text
      updateTrigger();
    }

    // Clear error states when user starts typing/selecting
    if (dom.inputs.name) {
      dom.inputs.name.addEventListener('input', () => {
        dom.inputs.name.classList.remove('is-invalid');
      });
    }
    if (dom.inputs.email) {
      dom.inputs.email.addEventListener('input', () => {
        dom.inputs.email.classList.remove('is-invalid');
      });
    }
    if (dom.inputs.message) {
      dom.inputs.message.addEventListener('input', () => {
        dom.inputs.message.classList.remove('is-invalid');
      });
    }
    if (dom.inputs.orderNumber) {
      // Only allow numeric input and limit to 10 digits
      dom.inputs.orderNumber.addEventListener('input', (e) => {
        // Remove any non-numeric characters and limit to 10 digits
        e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
        dom.inputs.orderNumber.classList.remove('is-invalid');
      });
      
      // Prevent non-numeric characters on paste and limit to 10 digits
      dom.inputs.orderNumber.addEventListener('paste', (e) => {
        e.preventDefault();
        const paste = (e.clipboardData || window.clipboardData).getData('text');
        const numericOnly = paste.replace(/[^0-9]/g, '').slice(0, 10);
        e.target.value = numericOnly;
      });
    }

    const defaults = getCustomerDefaults();
    if (dom.inputs.name) {
      dom.inputs.name.value = defaults.name;
    }
    if (dom.inputs.email) {
      dom.inputs.email.value = defaults.email;
    }

    // Handle subject change to show/hide order number field
    if (dom.inputs.subject) {
      dom.inputs.subject.addEventListener('change', (event) => {
        const isOrderInquiry = event.target.value === 'Inquiry about my order';
        if (dom.inputs.orderNumberWrapper) {
          dom.inputs.orderNumberWrapper.style.display = isOrderInquiry ? 'block' : 'none';
        }
        if (dom.inputs.orderNumber) {
          if (isOrderInquiry) {
            dom.inputs.orderNumber.setAttribute('required', '');
          } else {
            dom.inputs.orderNumber.removeAttribute('required');
            dom.inputs.orderNumber.value = '';
          }
        }
      });
    }

    if (dom.form) {
      dom.form.addEventListener('submit', handleSubmit);
      
      // Clear FAQ search when any form field is focused
      const clearFaqSearch = () => {
        if (state.faqFilter && dom.faqList) {
          // Add fade-out animation to visible items
          const faqItems = dom.faqList.querySelectorAll('.wtf-support-widget__faq-single');
          if (faqItems.length > 0) {
            faqItems.forEach((item) => {
              item.classList.add('wtf-support-widget__faq-animate-out');
            });
            
            // Wait for animation to complete before clearing
            setTimeout(() => {
              state.faqFilter = '';
              if (dom.faqSearch) {
                dom.faqSearch.value = '';
              }
              renderFaqList();
            }, 200);
          } else {
            // No items to animate, clear immediately
            state.faqFilter = '';
            if (dom.faqSearch) {
              dom.faqSearch.value = '';
            }
            renderFaqList();
          }
        }
      };
      
      // Add focus listeners to all form inputs
      const formInputs = dom.form.querySelectorAll('input, textarea, select, .wtf-support-widget__select-trigger');
      formInputs.forEach((input) => {
        input.addEventListener('focus', clearFaqSearch);
      });
    }
    if (dom.faqSearch) {
      const debouncedSearch = debounce((value) => {
        state.faqFilter = value;
        renderFaqList();
      }, 300);
      
      dom.faqSearch.addEventListener('input', (event) => {
        debouncedSearch(event.target.value);
      });
    }
    root.appendChild(wrapper);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error rendering support widget panel:', error);
      // Still append wrapper even if there's an error
      if (wrapper && wrapper.parentNode !== root) {
        root.appendChild(wrapper);
      }
    }
  };

  const setStatus = (msg, tone = 'muted') => {
    if (!dom.status) return;
    dom.status.textContent = msg;
    dom.status.dataset.tone = tone;
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const clearFieldErrors = () => {
    // Remove error classes from all fields
    if (dom.inputs.name) dom.inputs.name.classList.remove('is-invalid');
    if (dom.inputs.email) dom.inputs.email.classList.remove('is-invalid');
    if (dom.inputs.message) dom.inputs.message.classList.remove('is-invalid');
    if (dom.inputs.orderNumber) dom.inputs.orderNumber.classList.remove('is-invalid');
    const customSelect = dom.form?.querySelector('.wtf-support-widget__custom-select');
    const selectTrigger = customSelect?.querySelector('.wtf-support-widget__select-trigger');
    if (selectTrigger) selectTrigger.classList.remove('is-invalid');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (state.loading) return;
    
    // Clear previous errors
    setStatus('', 'muted');
    clearFieldErrors();
    
    // Validate required fields
    const name = dom.inputs.name?.value.trim() || '';
    const email = dom.inputs.email?.value.trim() || '';
    const subject = dom.inputs.subject?.value.trim() || '';
    const message = dom.inputs.message?.value.trim() || '';
    const orderNumber = dom.inputs.orderNumber ? dom.inputs.orderNumber.value.trim() : '';
    
    // Validate name
    if (!name) {
      setStatus('Please enter your name.', 'error');
      if (dom.inputs.name) {
        dom.inputs.name.classList.add('is-invalid');
        dom.inputs.name.focus();
      }
      return;
    }
    
    if (name.length < 2) {
      setStatus('Name must be at least 2 characters.', 'error');
      if (dom.inputs.name) {
        dom.inputs.name.classList.add('is-invalid');
        dom.inputs.name.focus();
      }
      return;
    }
    
    // Validate email
    if (!email) {
      setStatus('Please enter your email address.', 'error');
      if (dom.inputs.email) {
        dom.inputs.email.classList.add('is-invalid');
        dom.inputs.email.focus();
      }
      return;
    }
    
    if (!validateEmail(email)) {
      setStatus('Please enter a valid email address.', 'error');
      if (dom.inputs.email) {
        dom.inputs.email.classList.add('is-invalid');
        dom.inputs.email.focus();
      }
      return;
    }
    
    // Validate subject
    if (!subject) {
      setStatus('Please select a subject.', 'error');
      const customSelect = dom.form?.querySelector('.wtf-support-widget__custom-select');
      const selectTrigger = customSelect?.querySelector('.wtf-support-widget__select-trigger');
      if (selectTrigger) {
        selectTrigger.classList.add('is-invalid');
        selectTrigger.focus();
      }
      return;
    }
    
    // Check if order number is required but empty
    const isOrderInquiry = subject === 'Inquiry about my order';
    if (isOrderInquiry && !orderNumber) {
      setStatus('Please provide your order number.', 'error');
      if (dom.inputs.orderNumber) {
        dom.inputs.orderNumber.classList.add('is-invalid');
        dom.inputs.orderNumber.focus();
      }
      return;
    }
    
    // Validate message
    if (!message) {
      setStatus('Please enter your message.', 'error');
      if (dom.inputs.message) {
        dom.inputs.message.classList.add('is-invalid');
        dom.inputs.message.focus();
      }
      return;
    }
    
    if (message.length < 10) {
      setStatus('Message must be at least 10 characters.', 'error');
      if (dom.inputs.message) {
        dom.inputs.message.classList.add('is-invalid');
        dom.inputs.message.focus();
      }
      return;
    }
    
    const proxy = safeProxy('/conversations');
    if (!proxy) {
      setStatus('Proxy endpoint missing. Please configure the widget.', 'error');
      return;
    }

    state.loading = true;
    setStatus('Sending message…', 'info');

    // Format order number with prefix if provided
    const formattedOrderNumber = isOrderInquiry && orderNumber ? `WTF/N${orderNumber}` : null;

    // Collect browser information
    const browserInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      screenWidth: window.screen?.width,
      screenHeight: window.screen?.height,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    };

    const payload = {
      name: name,
      email: email,
      subject: subject,
      message: message,
      channel: config.defaultChannel || undefined,
      metadata: {
        shop: config.shop || {},
        path: window.location.pathname,
        url: window.location.href,
        browser: browserInfo,
        ...(formattedOrderNumber ? { order_number: formattedOrderNumber } : {}),
      },
    };

    try {
      const response = await fetch(proxy, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(errorBody || response.statusText);
      }

      // Reset form
      const defaults = getCustomerDefaults();
      if (dom.form) {
        dom.form.reset();
      }
      if (dom.inputs.name) {
        dom.inputs.name.value = defaults.name;
      }
      if (dom.inputs.email) {
        dom.inputs.email.value = defaults.email;
      }
      
      // Reset custom dropdown
      const customSelect = dom.form?.querySelector('.wtf-support-widget__custom-select');
      const selectText = customSelect?.querySelector('.wtf-support-widget__select-text');
      const selectTrigger = customSelect?.querySelector('.wtf-support-widget__select-trigger');
      if (selectText && selectTrigger && dom.inputs.subject) {
        dom.inputs.subject.value = '';
        selectText.textContent = 'Subject';
        selectTrigger.classList.remove('has-value');
      }
      
      // Hide order number field
      if (dom.inputs.orderNumberWrapper) {
        dom.inputs.orderNumberWrapper.style.display = 'none';
      }
      
      setStatus('Thanks! We\'ll get back to you shortly.', 'success');
      
      // Scroll to top of panel to show success message
      if (dom.panel) {
        dom.panel.scrollTop = 0;
      }
    } catch {
      setStatus('Something went wrong. Please try again.', 'error');
    } finally {
      state.loading = false;
    }
  };

  const loadFaqs = async () => {
    const endpoint = safeProxy('/articles?limit=100');
    if (!endpoint) {
      return;
    }

    try {
      const response = await fetch(endpoint, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error('Failed to load FAQs');
      }

      const { articles = [] } = await response.json();
      state.faqArticles = articles;
      renderFaqList();
      state.faqLoaded = true;
    } catch {
      dom.faqList.innerHTML = '<li>Unable to load FAQ entries.</li>';
    }
  };

  // Simple markdown to HTML converter
  const markdownToHtml = (text) => {
      if (!text) return '';
      
      // If content is already HTML (contains tags), return as-is
      if (/<[a-z][\s\S]*>/i.test(text)) {
        return text;
      }
      
      // Trim and normalize whitespace
      text = text.trim();
      
      // Normalize line endings
      text = text.replace(/\r\n/g, '\n');
      
      // Process code blocks first (preserve their content)
      const codeBlocks = [];
      text = text.replace(/```([\s\S]*?)```/g, (match, code) => {
        const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
        codeBlocks.push(`<pre><code>${code.trim()}</code></pre>`);
        return placeholder;
      });
      
      // Split into blocks (by double newlines)
      let blocks = text.split(/\n\n+/).map(block => block.trim()).filter(block => block);
      
      // Merge consecutive numbered list items (they may be split by blank lines)
      const mergedBlocks = [];
      let currentList = [];
      let inOrderedList = false;
      
      for (const block of blocks) {
        if (/^\d+\.\s+/.test(block)) {
          // This is a numbered list item
          currentList.push(block);
          inOrderedList = true;
        } else {
          // Not a list item - flush any accumulated list
          if (inOrderedList && currentList.length > 0) {
            mergedBlocks.push(currentList.join('\n'));
            currentList = [];
            inOrderedList = false;
          }
          mergedBlocks.push(block);
        }
      }
      
      // Don't forget the last list if there is one
      if (currentList.length > 0) {
        mergedBlocks.push(currentList.join('\n'));
      }
      
      blocks = mergedBlocks;
      
      // Process each block
      const processedBlocks = blocks.map(block => {
        // Check for headings (must be checked before inline processing)
        if (/^######\s+(.+)$/.test(block)) {
          return block.replace(/^######\s+(.+)$/, '<h6>$1</h6>');
        }
        if (/^#####\s+(.+)$/.test(block)) {
          return block.replace(/^#####\s+(.+)$/, '<h5>$1</h5>');
        }
        if (/^####\s+(.+)$/.test(block)) {
          return block.replace(/^####\s+(.+)$/, '<h4>$1</h4>');
        }
        if (/^###\s+(.+)$/.test(block)) {
          return block.replace(/^###\s+(.+)$/, '<h3>$1</h3>');
        }
        if (/^##\s+(.+)$/.test(block)) {
          return block.replace(/^##\s+(.+)$/, '<h2>$1</h2>');
        }
        if (/^#\s+(.+)$/.test(block)) {
          return block.replace(/^#\s+(.+)$/, '<h1>$1</h1>');
        }
        
        // Check for mixed blocks with list items (e.g., "intro text:\n- item\n- item")
        const lines = block.split('\n');
        const hasListItems = lines.some(line => /^[-*]\s+/.test(line.trim()));
        
        if (hasListItems && lines.length > 1) {
          let html = '';
          let currentList = [];
          let currentListType = null;

          const flushList = () => {
            if (!currentList.length) return;
            const listItems = currentList
              .map((item) => {
                const nestedHtml = item.nested && item.nested.length
                  ? `<ul>${item.nested.map((nested) => `<li>${nested}</li>`).join('')}</ul>`
                  : '';
                return `<li>${item.content}${nestedHtml}</li>`;
              })
              .join('');
            html += currentListType === 'ol' ? `<ol>${listItems}</ol>` : `<ul>${listItems}</ul>`;
            currentList = [];
            currentListType = null;
          };

          lines.forEach((line) => {
            const trimmed = line.trim();
            if (!trimmed) {
              return;
            }

            if (/^\d+\.\s+/.test(trimmed)) {
              const content = processInlineMarkdown(trimmed.replace(/^\d+\.\s+/, ''));
              if (currentListType !== 'ol') {
                flushList();
                currentListType = 'ol';
              }
              currentList.push({ content, nested: [] });
              return;
            }

            if (/^[-*]\s+/.test(trimmed)) {
              const content = processInlineMarkdown(trimmed.replace(/^[-*]\s+/, ''));
              if (currentListType === 'ol' && currentList.length > 0) {
                currentList[currentList.length - 1].nested.push(content);
              } else {
                if (currentListType !== 'ul') {
                  flushList();
                  currentListType = 'ul';
                }
                currentList.push({ content, nested: [] });
              }
              return;
            }

            // Non-list line - close any open list, add as paragraph
            flushList();
            html += `<p>${processInlineMarkdown(trimmed)}</p>`;
          });

          flushList();
          return html;
        }
        
        // Check for pure unordered lists (all lines start with - or *)
        const unorderedListItems = block.split('\n').filter(line => /^[-*]\s+/.test(line.trim()));
        if (unorderedListItems.length > 0 && unorderedListItems.length === block.split('\n').length) {
          const items = unorderedListItems.map(line => {
            const content = line.replace(/^[-*]\s+/, '').trim();
            return `<li>${processInlineMarkdown(content)}</li>`;
          }).join('');
          return `<ul>${items}</ul>`;
        }
        
        // Check for pure ordered lists (all lines start with number.)
        const orderedListItems = block.split('\n').filter(line => /^\d+\.\s+/.test(line.trim()));
        if (orderedListItems.length > 0 && orderedListItems.length === block.split('\n').length) {
          const items = orderedListItems.map(line => {
            const content = line.replace(/^\d+\.\s+/, '').trim();
            return `<li>${processInlineMarkdown(content)}</li>`;
          }).join('');
          return `<ol>${items}</ol>`;
        }
        
        // Regular paragraph - process inline markdown
        return `<p>${processInlineMarkdown(block)}</p>`;
      }).join('');
      
      // Restore code blocks
      let result = processedBlocks;
      codeBlocks.forEach((code, index) => {
        result = result.replace(`__CODE_BLOCK_${index}__`, code);
      });
      
      return result;
  };
  
  // Process inline markdown (bold, italic, links, code)
  const processInlineMarkdown = (text) => {
    return text
      // Bold (must come before italic to avoid conflicts)
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__(.*?)__/g, '<strong>$1</strong>')
      // Italic (single asterisk/underscore)
      .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
      .replace(/_([^_]+?)_/g, '<em>$1</em>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code>$1</code>');
  };

  const renderFaqList = () => {
    if (!dom.faqList) return;
    const term = state.faqFilter.trim().toLowerCase();
    
    // Hide FAQ list if no search term or too short
    if (!term || term.length < 2) {
      dom.faqList.innerHTML = '';
      dom.faqList.style.display = 'none';
      return;
    }
    
    dom.faqList.style.display = 'flex';
    
    let articles = state.faqArticles;
    articles = articles.filter((article) => {
      // Search only in title
      const title = (article.title || '').toLowerCase();
      return title.includes(term);
    });

    if (!articles.length) {
      dom.faqList.innerHTML = '<li class="wtf-support-widget__faq-empty">No quick answers found.</li>';
      return;
    }

    // Sort articles alphabetically by title
    articles.sort((a, b) => {
      const titleA = (a.title || '').toLowerCase();
      const titleB = (b.title || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });

    // Group articles by topic (currently unused, but kept for future feature)
    // const grouped = articles.reduce((acc, article) => {
    //   const topic = article.topic?.name || 'General';
    //   acc[topic] = acc[topic] || [];
    //   acc[topic].push(article);
    //   return acc;
    // }, {});

    dom.faqList.innerHTML = articles
      .slice(0, 8)
      .map(
        (article, index) => {
          const hasContent = article.body || article.content || article.html_body || article.text_body;
          const articleId = `faq-${index}`;
          const content = article.body || article.content || article.html_body || article.text_body || '';
          const animationDelay = `style="animation-delay: ${index * 0.05}s"`;
          
          if (hasContent) {
            // Convert markdown to HTML if needed
            const htmlContent = markdownToHtml(content);
            
            return `
              <li class="wtf-support-widget__faq-single wtf-support-widget__faq-animate" ${animationDelay}>
                <button class="wtf-support-widget__faq-toggle" type="button" aria-expanded="false" aria-controls="${articleId}-content">
                  <span class="wtf-support-widget__faq-title">${article.title}</span>
                  <svg class="wtf-support-widget__faq-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                    <path fill="currentColor" d="M8 12L2 6l1.41-1.41L8 9.17l4.59-4.58L14 6l-6 6z"/>
                  </svg>
                </button>
                <div class="wtf-support-widget__faq-content" id="${articleId}-content" hidden>
                  <div class="wtf-support-widget__faq-body">${htmlContent}</div>
                  ${article.public_url ? `<a href="${article.public_url}" target="_blank" rel="noopener" class="wtf-support-widget__faq-link">Read full article →</a>` : ''}
                </div>
              </li>
            `;
          } else {
            // Fallback to link if no content available
            return `
              <li class="wtf-support-widget__faq-single wtf-support-widget__faq-animate" ${animationDelay}>
                <a href="${article.public_url || '#'}" target="_blank" rel="noopener">
                  <span class="wtf-support-widget__faq-title">${article.title}</span>
                </a>
              </li>
            `;
          }
        },
      )
      .join('');
    
    // Add click handlers for expandable items
    dom.faqList.querySelectorAll('.wtf-support-widget__faq-toggle').forEach((button) => {
      button.addEventListener('click', () => {
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        const content = document.getElementById(button.getAttribute('aria-controls'));
        const willBeExpanded = !isExpanded;
        
        // If expanding this item, close all others first
        if (willBeExpanded) {
          dom.faqList.querySelectorAll('.wtf-support-widget__faq-toggle').forEach((otherButton) => {
            if (otherButton !== button) {
              const otherContent = document.getElementById(otherButton.getAttribute('aria-controls'));
              otherButton.setAttribute('aria-expanded', 'false');
              otherContent.setAttribute('hidden', '');
              otherButton.classList.remove('is-expanded');
            }
          });
        }
        
        button.setAttribute('aria-expanded', String(willBeExpanded));
        
        if (willBeExpanded) {
          // Expanding: animate button first, then content
          button.classList.add('is-expanded');
          // Small delay to let button border animation start first
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (button.getAttribute('aria-expanded') === 'true') {
                content.removeAttribute('hidden');
              }
            });
          });
        } else {
          // Collapsing: start content collapse immediately
          content.setAttribute('hidden', '');
          // Delay button animation to sync with content collapse
          setTimeout(() => {
            if (button.getAttribute('aria-expanded') === 'false') {
              button.classList.remove('is-expanded');
            }
          }, 250);
        }
      });
    });
  };

  const loadEmbedSdk = () => {
    if (!config.brand || document.querySelector('script[data-reamaze-sdk]')) {
      return;
    }

    window.Reamaze = window.Reamaze || [];
    window.Reamaze.push(['setAccount', config.brand]);
    if (config.defaultChannel) {
      window.Reamaze.push(['setDefaultChannel', config.defaultChannel]);
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.reamaze.com/assets/reamaze.js';
    script.defer = true;
    script.setAttribute('data-reamaze-sdk', 'true');
    document.head.appendChild(script);
  };
  
  const handleLauncherScroll = () => {
    if (!dom.launcher) return;
    
    const scrollThreshold = 200; // Show after scrolling 200px
    const scrolled = window.scrollY || window.pageYOffset;
    
    if (scrolled > scrollThreshold) {
      dom.launcher.classList.add('is-visible');
      // Remove listener once shown - no need to keep checking
      window.removeEventListener('scroll', handleLauncherScroll);
    }
  };

  /**
   * Add UTM parameters to WTF Notebooks URLs for tracking.
   * Preserves existing UTM params from the current page, or adds default AI assistant UTMs.
   */
  const addUTMToLinks = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const links = doc.querySelectorAll('a[href^="https://wtfnotebooks.com"], a[href^="http://wtfnotebooks.com"], a[href^="/"]');
    
    // Get current page UTMs if they exist
    const currentUrl = new URL(window.location.href);
    const hasExistingUTM = currentUrl.searchParams.has('utm_source');
    
    links.forEach(link => {
      try {
        const url = new URL(link.href, window.location.origin);
        
        // Only modify WTF Notebooks URLs
        if (!url.hostname.includes('wtfnotebooks.com') && url.hostname !== window.location.hostname) {
          return;
        }
        
        // If the link already has UTMs, leave it alone
        if (url.searchParams.has('utm_source')) {
          return;
        }
        
        // Forward existing UTMs from current page, or use AI assistant defaults
        if (hasExistingUTM) {
          // Forward UTMs from current page
          ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(param => {
            const value = currentUrl.searchParams.get(param);
            if (value) {
              url.searchParams.set(param, value);
            }
          });
        } else {
          // Add AI assistant UTMs
          url.searchParams.set('utm_source', 'WTF Notebooks');
          url.searchParams.set('utm_medium', 'ai-assistant');
          url.searchParams.set('utm_campaign', 'ai-assistant');
        }
        
        link.href = url.toString();
      } catch {
        // Invalid URL, skip
      }
    });
    
    return doc.body.innerHTML;
  };

  const showAIMessage = (htmlContent) => {
    if (!dom.aiAnswer) return;
    
    // Expand container if not already visible (first question)
    const container = document.querySelector('.wtf-ai-response-container');
    if (container && !container.classList.contains('visible')) {
      container.classList.add('visible');
    }
    
    // Reset opacity for fade effect
    dom.aiAnswer.style.opacity = '0';
    
    // Add UTM parameters to links before displaying
    const processedContent = addUTMToLinks(htmlContent);
    
    // Set content (replaces thinking overlay if present)
    dom.aiAnswer.innerHTML = processedContent;
    
    // Fade in content
    setTimeout(() => {
      dom.aiAnswer.style.opacity = '1';
      dom.aiAnswer.classList.add('fade-in');
    }, 100);
  };

  const handleAIQuestion = async () => {
    const question = dom.aiInput?.value.trim();
    
    if (!question) {
      return;
    }
    
    if (state.aiLoading) {
      return;
    }
    
    // Abuse prevention: Limit question length
    if (question.length > 500) {
      dom.aiAnswer.innerHTML = '<p style="color: #d32f2f;">Whoa there, Shakespeare! 📚 That question is way too long. Can you trim it down to under 500 characters? I promise I\'ll still understand you.</p>';
      dom.aiResponse.style.display = 'block';
      return;
    }
    
    // Abuse prevention: Rate limiting (max 25 questions per session)
    if (!state.aiQuestionCount) {
      state.aiQuestionCount = 0;
    }
    state.aiQuestionCount++;
    
    if (state.aiQuestionCount > 25) {
      dom.aiAnswer.innerHTML = '<p style="color: #d32f2f;">Okay, I think we need to take this relationship to the next level. 😅 You\'ve asked me 25 questions, which is amazing, but now I think you should probably talk to a real human. Use the contact form below and our team will take great care of you!</p>';
      dom.aiResponse.style.display = 'block';
      return;
    }
    
    // Honeypot check (bots fill invisible fields, humans don't)
    const honeypotValue = dom.aiHoneypot?.value || '';
    if (honeypotValue.length > 0) {
      // Silently block - bots shouldn't know they're caught
      return;
    }
    
    // Timing check (< 2 seconds from first interaction = suspicious)
    const interactionTime = state.aiFirstInteraction ? Date.now() - state.aiFirstInteraction : null;
    if (interactionTime !== null && interactionTime < 2000) {
      // Silently block - bots shouldn't know they're caught
      return;
    }
    
    // For local dev: call AI service directly (browser is on local network)
    // For prod: use safeProxy to go through Cloudflare Worker
    const isLocalDev = window.location.hostname === 'localhost' || window.location.port === '9292';
    const isStaging = window.location.hostname === 'wtf-notebooks.devops.jaydeeco.com';
    const aiEndpoint = isLocalDev
      ? 'http://localhost:8200/ai/answer'
      : isStaging
        ? 'https://ai-assistant.devops.jaydeeco.com/ai/answer'
        : safeProxy('/ai/answer');
    
    if (!aiEndpoint) {
      dom.aiAnswer.innerHTML = '<p style="color: #d32f2f;">Oops! 🤖 My brain seems to be taking a coffee break. Try again in a moment, or use the contact form below if you need help right away!</p>';
      dom.aiResponse.style.display = 'block';
      return;
    }
    
    state.aiLoading = true;
    dom.aiAskBtn.textContent = 'Sending...';
    dom.aiAskBtn.disabled = true;
    dom.aiAskBtn.style.background = '#058828'; // Green while sending
    
    // If answer card already exists (subsequent questions), show spinner overlay
    const container = document.querySelector('.wtf-ai-response-container');
    const hasAnswerCard = container?.classList.contains('visible');
    
    if (hasAnswerCard && dom.aiAnswer) {
      // Answer card already visible - just add spinner overlay on top
      const existingCard = dom.aiAnswer.querySelector('.wtf-ai-answer-card');
      if (existingCard) {
        // Add overlay to existing card
        const overlay = document.createElement('div');
        overlay.className = 'wtf-ai-thinking-overlay';
        overlay.innerHTML = '<div class="wtf-ai-spinner"></div>';
        existingCard.appendChild(overlay);
      }
    }
    // For first question, container will expand when showAIMessage() is called with the answer
    
    // FAQs are handled by AI assistant's Redis cache → Re:amaze API flow
    // No need to fetch them here (avoids redundant API calls)
    
    // Get customer's timezone for accurate date calculations
    let customerTimezone = null;
    try {
      customerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      // Fallback to null if timezone detection fails
      customerTimezone = null;
    }

    const payload = {
      user_message: question,
      locale: config.locale || 'en',
      customer_timezone: customerTimezone, // Customer's browser timezone for date calculations
      page_context: {
        url: window.location.href,
        path: window.location.pathname,
      },
      // faq_candidates removed - AI assistant handles via Redis cache → Re:amaze API
      conversation_history: state.aiConversation || [], // Phase 2 Security: conversation-based verification
      // Anti-bot security fields
      honeypot_value: honeypotValue, // Should be empty for humans
      interaction_time_ms: interactionTime, // Should be > 2000ms for humans
    };
    
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };
      
      // Add API key for direct AI service calls (local dev and staging)
      if (isLocalDev || isStaging) {
        headers['X-API-Key'] = 'wtf-ai-2024-9c8e4b7a3f1d6e5a2b9c8d7e6f5a4b3c2d1e0f9a8b7c6d5e4f3a2b1c0d9e8f7a6b';
      }
      
      // Add 30 second timeout to handle slow cold starts (machine wake-up)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      let response;
      try {
        response = await fetch(aiEndpoint, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
      } catch (fetchErr) {
        clearTimeout(timeoutId);
        if (fetchErr.name === 'AbortError') {
          throw new Error('Request timed out after 30 seconds. The AI service may be waking up - please try again.');
        }
        throw fetchErr;
      }
      
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(errorBody || response.statusText);
      }
      
      const data = await response.json();
      state.aiAnswer = data;
      
      // Display answer with animations
      let answerHTML = `<div class="wtf-ai-answer-card">`;
      answerHTML += markdownToHtml(data.answer_text);
      
      if (data.confidence !== undefined) {
        const confidenceColor = data.confidence >= 0.8 ? '#4caf50' : data.confidence >= 0.6 ? '#ff9800' : '#f44336';
        answerHTML += `<span class="wtf-confidence-dot" style="background-color: ${confidenceColor};" title="Confidence: ${Math.round(data.confidence * 100)}%"></span>`;
      }
      answerHTML += `</div>`;
      
      showAIMessage(answerHTML);
      
      // Phase 2 Security: Save conversation history for context
      state.aiConversation.push(
        { role: 'user', content: question },
        { role: 'assistant', content: data.answer_text }
      );
      
      // Keep only last 20 message pairs (40 messages total) to avoid token limits
      if (state.aiConversation.length > 40) {
        state.aiConversation = state.aiConversation.slice(-40);
      }
      
    } catch (err) {
      
      // Report client-side error to backend for monitoring
      try {
        const isLocalDev = window.location.hostname === 'localhost' || window.location.port === '9292';
        const isStaging = window.location.hostname === 'wtf-notebooks.devops.jaydeeco.com';
        const errorEndpoint = isLocalDev
          ? 'http://localhost:8200/ai/client-error'
          : isStaging
            ? 'https://ai-assistant.devops.jaydeeco.com/ai/client-error'
            : safeProxy('/ai/client-error');
        
        if (errorEndpoint) {
          fetch(errorEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              error_type: err.name || 'ClientError',
              error_message: err.message || 'Unknown error',
              page_url: window.location.href,
              user_agent: navigator.userAgent,
            }),
          }).catch(() => {}); // Silent fail - don't interrupt user
        }
      } catch {
        // Silent fail
      }
      
      const errorHTML = `<div class="wtf-ai-answer-card"><p style="color: #d32f2f; margin: 0;">Well, this is awkward... 😬 Something went wrong on my end. Could you try asking that again? If I keep messing up, our lovely humans below can definitely help you out!</p></div>`;
      showAIMessage(errorHTML);
    } finally {
      state.aiLoading = false;
      dom.aiAskBtn.textContent = 'Send';
      dom.aiAskBtn.style.background = '#D32F2F'; // Reset to red
      
      // Reset hasTyped flag so next question starts fresh
      state.aiHasTyped = false;
      dom.aiAskBtn.disabled = false;
    }
  };
  
  // Utility function for escaping HTML (currently unused, but kept for potential future use)
  // const escapeHtml = (text) => {
  //   const div = document.createElement('div');
  //   div.textContent = text;
  //   return div.innerHTML;
  // };

  const init = () => {
    renderBackdrop();
    renderPanel();
    renderLauncher();
    
    // On mobile, hide launcher initially and show after scrolling
    if (isMobile()) {
      dom.launcher.classList.add('hidden-on-mobile');
      window.addEventListener('scroll', handleLauncherScroll, { passive: true });
      // Also check immediately in case user is already scrolled
      handleLauncherScroll();
    }
  };

  init();
})();
